import { Turnstile } from '@marsidev/react-turnstile';
import { useState } from 'react';
import { CAPTCHA_SITE_KEY, CaptchaStatus } from './Captcha.consts';
import classes from './Captcha.module.css';
import type { Props } from './Captcha.types';

export const Captcha = ({ onSuccess }: Props) => {
  const [captchaStatus, setCaptchaStatus] = useState(CaptchaStatus.HIDDEN);

  const captchaResolved = captchaStatus === CaptchaStatus.SUCCESS;
  const isLoading = captchaStatus === CaptchaStatus.LOADING;

  const resetCaptcha = () => {
    setCaptchaStatus(CaptchaStatus.LOADED);
  };

  const handleLoadScript = () => {
    setCaptchaStatus(CaptchaStatus.LOADING);
  };

  const handleWidgetLoad = () => {
    setTimeout(() => {
      setCaptchaStatus(CaptchaStatus.LOADED);
    }, 500);
  };

  const handleTurnstileSuccess = () => {
    setCaptchaStatus(CaptchaStatus.SUCCESS);
    onSuccess?.();
  };

  const handleTurnstileError = () => {
    resetCaptcha();
  };

  const handleTurnstileExpire = () => {
    resetCaptcha();
  };

  if (captchaResolved) {
    return null;
  }

  return (
    <>
      {isLoading ? (
        <div className={classes.captchaWrapper}>
          <div className={classes.loadingWrapper}>
            <svg
              aria-hidden="true"
              className={classes.spinner}
              fill="none"
              viewBox="0 0 30 30"
              xmlns="http://www.w3.org/2000/svg"
            >
              <line
                className={classes.circle}
                x1="15"
                x2="15"
                y1="1.5"
                y2="5.5"
              />
              <line
                className={classes.circle}
                transform="rotate(45 24.5459 5.45405)"
                x1="24.5459"
                x2="24.5459"
                y1="5.45405"
                y2="10.45405"
              />
              <line
                className={classes.circle}
                transform="rotate(90 28.5 15)"
                x1="28.5"
                x2="28.5"
                y1="15"
                y2="20"
              />
              <line
                className={classes.circle}
                transform="rotate(135 24.5459 24.546)"
                x1="24.5459"
                x2="24.5459"
                y1="24.546"
                y2="29.546"
              />
              <line
                className={classes.circle}
                transform="rotate(180 15 28.5)"
                x1="15"
                x2="15"
                y1="28.5"
                y2="33.5"
              />
              <line
                className={classes.circle}
                transform="rotate(-135 5.4541 24.5459)"
                x1="5.4541"
                x2="5.4541"
                y1="24.5459"
                y2="29.5459"
              />
              <line
                className={classes.circle}
                transform="rotate(-90 1.5 15)"
                x1="1.5"
                x2="1.5"
                y1="15"
                y2="20"
              />
              <line
                className={classes.circle}
                transform="rotate(-45 5.45408 5.45404)"
                x1="5.45408"
                x2="5.45408"
                y1="5.45404"
                y2="10.45404"
              />
            </svg>
            <div className={classes.text}>Loading...</div>
          </div>
          <div className={classes.brandWrapper}>
            <a
              className={classes.brand}
              href="https://www.cloudflare.com/products/turnstile/?utm_source=turnstile&amp;utm_campaign=widget"
              rel="noopener noreferrer"
              target="_blank"
            >
              <svg
                aria-label="Cloudflare"
                fill="none"
                role="img"
                viewBox="0 0 73 25"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M61.8848 15.7841L62.0632 15.1578C62.2758 14.4126 62.1967 13.7239 61.8401 13.2178C61.5118 12.7517 60.9649 12.4773 60.3007 12.4453L47.7201 12.2836C47.6811 12.2829 47.6428 12.2728 47.6083 12.2542C47.5738 12.2356 47.5442 12.209 47.5217 12.1766C47.4996 12.1431 47.4856 12.1049 47.4807 12.0649C47.4758 12.025 47.4801 11.9844 47.4933 11.9465C47.5149 11.8839 47.5541 11.8291 47.6061 11.7888C47.658 11.7486 47.7204 11.7247 47.7856 11.72L60.4827 11.5566C61.9889 11.4864 63.6196 10.2462 64.1905 8.73372L64.9146 6.81361C64.9443 6.73242 64.951 6.64444 64.9341 6.55957C64.112 2.80652 60.8115 0 56.8652 0C53.2293 0 50.1421 2.38158 49.0347 5.69186C48.2864 5.12186 47.3535 4.85982 46.4228 4.95823C44.6785 5.13401 43.276 6.55928 43.1034 8.32979C43.059 8.77189 43.0915 9.21845 43.1992 9.64918C40.3497 9.73347 38.0645 12.1027 38.0645 15.0151C38.0649 15.2751 38.0838 15.5347 38.1212 15.7919C38.1294 15.8513 38.1584 15.9057 38.2029 15.9452C38.2474 15.9847 38.3044 16.0067 38.3635 16.0071L61.5894 16.0099C61.5916 16.0101 61.5938 16.0101 61.596 16.0099C61.6616 16.0088 61.7252 15.9862 61.7772 15.9455C61.8293 15.9049 61.867 15.8483 61.8848 15.7841Z"
                  fill="#F6821F"
                />
                <path
                  d="M66.0758 6.95285C65.9592 6.95285 65.843 6.95582 65.7274 6.96177C65.7087 6.96312 65.6904 6.96719 65.6729 6.97385C65.6426 6.98437 65.6152 7.00219 65.5931 7.02579C65.5711 7.04939 65.555 7.07806 65.5462 7.10936L65.0515 8.84333C64.8389 9.58847 64.918 10.2766 65.2749 10.7827C65.6029 11.2494 66.1498 11.5233 66.814 11.5552L69.4959 11.7186C69.5336 11.7199 69.5705 11.73 69.6037 11.7483C69.6369 11.7666 69.6654 11.7925 69.687 11.8239C69.7092 11.8576 69.7234 11.896 69.7283 11.9363C69.7332 11.9765 69.7288 12.0173 69.7153 12.0555C69.6937 12.118 69.6546 12.1727 69.6028 12.2129C69.5509 12.2531 69.4887 12.2771 69.4236 12.2819L66.6371 12.4453C65.1241 12.5161 63.4937 13.7558 62.9233 15.2682L62.722 15.8022C62.7136 15.8245 62.7105 15.8486 62.713 15.8724C62.7155 15.8961 62.7236 15.9189 62.7365 15.9389C62.7495 15.9589 62.7669 15.9755 62.7874 15.9873C62.8079 15.9991 62.8309 16.0058 62.8544 16.0068C62.8569 16.0068 62.8592 16.0068 62.8618 16.0068H72.4502C72.506 16.0073 72.5604 15.9893 72.6051 15.9554C72.6498 15.9216 72.6823 15.8739 72.6977 15.8195C72.8677 15.2043 72.9535 14.5684 72.9529 13.9296C72.9517 10.0767 69.8732 6.95285 66.0758 6.95285Z"
                  fill="#FBAD41"
                />
                <path
                  className={classes.logoText}
                  d="M8.11963 18.8904H9.75541V23.4254H12.6139V24.8798H8.11963V18.8904Z"
                />
                <path
                  className={classes.logoText}
                  d="M14.3081 21.9023V21.8853C14.3081 20.1655 15.674 18.7704 17.4952 18.7704C19.3164 18.7704 20.6653 20.1482 20.6653 21.8681V21.8853C20.6653 23.6052 19.2991 24.9994 17.4785 24.9994C15.6578 24.9994 14.3081 23.6222 14.3081 21.9023ZM18.9958 21.9023V21.8853C18.9958 21.0222 18.3806 20.2679 17.4785 20.2679C16.5846 20.2679 15.9858 21.0038 15.9858 21.8681V21.8853C15.9858 22.7484 16.6013 23.5025 17.4952 23.5025C18.3973 23.5025 18.9958 22.7666 18.9958 21.9023Z"
                />
                <path
                  className={classes.logoText}
                  d="M22.6674 22.253V18.8901H24.3284V22.2191C24.3284 23.0822 24.7584 23.4939 25.4159 23.4939C26.0733 23.4939 26.5034 23.1003 26.5034 22.2617V18.8901H28.1647V22.2093C28.1647 24.1432 27.0772 24.9899 25.3991 24.9899C23.7211 24.9899 22.6674 24.1268 22.6674 22.2522"
                />
                <path
                  className={classes.logoText}
                  d="M30.668 18.8907H32.9445C35.0526 18.8907 36.275 20.1226 36.275 21.8508V21.8684C36.275 23.5963 35.0355 24.88 32.911 24.88H30.668V18.8907ZM32.97 23.4076C33.9483 23.4076 34.597 22.8609 34.597 21.8928V21.8759C34.597 20.9178 33.9483 20.3614 32.97 20.3614H32.3038V23.4082L32.97 23.4076Z"
                />
                <path
                  className={classes.logoText}
                  d="M38.6525 18.8904H43.3738V20.3453H40.2883V21.3632H43.079V22.7407H40.2883V24.8798H38.6525V18.8904Z"
                />
                <path
                  className={classes.logoText}
                  d="M45.65 18.8904H47.2858V23.4254H50.1443V24.8798H45.65V18.8904Z"
                />
                <path
                  className={classes.logoText}
                  d="M54.4187 18.8475H55.9949L58.5079 24.8797H56.7541L56.3238 23.8101H54.047L53.6257 24.8797H51.9058L54.4187 18.8475ZM55.8518 22.5183L55.1941 20.8154L54.5278 22.5183H55.8518Z"
                />
                <path
                  className={classes.logoText}
                  d="M60.6149 18.8901H63.4056C64.3083 18.8901 64.9317 19.13 65.328 19.5406C65.6742 19.883 65.8511 20.3462 65.8511 20.9357V20.9526C65.8511 21.8678 65.3691 22.4754 64.6369 22.7919L66.045 24.88H64.1558L62.9671 23.0658H62.2507V24.88H60.6149V18.8901ZM63.3299 21.7654C63.8864 21.7654 64.2071 21.4915 64.2071 21.0551V21.0381C64.2071 20.5674 63.8697 20.328 63.3211 20.328H62.2507V21.7665L63.3299 21.7654Z"
                />
                <path
                  className={classes.logoText}
                  d="M68.2112 18.8904H72.9578V20.3024H69.8302V21.209H72.6632V22.5183H69.8302V23.4683H73V24.8798H68.2112V18.8904Z"
                />
                <path
                  className={classes.logoText}
                  d="M4.53824 22.6043C4.30918 23.13 3.82723 23.5022 3.18681 23.5022C2.29265 23.5022 1.67746 22.7493 1.67746 21.8851V21.8678C1.67746 21.0047 2.27593 20.2676 3.1698 20.2676C3.84367 20.2676 4.35681 20.6882 4.5734 21.2605H6.29764C6.02151 19.8349 4.78716 18.7707 3.18681 18.7707C1.36533 18.7707 0 20.1666 0 21.8851V21.9021C0 23.6219 1.3486 25 3.1698 25C4.72762 25 5.94525 23.9764 6.26645 22.6046L4.53824 22.6043Z"
                />
              </svg>
            </a>
            <div className={classes.links}>
              <a
                href="https://www.cloudflare.com/privacypolicy/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy
              </a>
              <span className={classes.linkSpacer}> • </span>
              <a
                href="https://www.cloudflare.com/website-terms/"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms
              </a>
            </div>
          </div>
        </div>
      ) : null}
      <Turnstile
        className={isLoading ? classes.hidden : ''}
        onError={handleTurnstileError}
        onExpire={handleTurnstileExpire}
        onLoadScript={handleLoadScript}
        onSuccess={handleTurnstileSuccess}
        onWidgetLoad={handleWidgetLoad}
        siteKey={CAPTCHA_SITE_KEY}
      />
    </>
  );
};
